import React from 'react';
import { Box, Text, useTheme } from '@chakra-ui/react';
import {
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
  Bar,
} from 'recharts';
import { useAxisStyles } from 'src/hooks/colors/useChartColors';
import { useGetDateAxisFormatter } from 'src/features/MM/hooks/useGetDateAxisFormatter';
import { CustomActiveDot } from 'src/components/atoms/graphs/CustomActiveDot';
import { ChartTooltip } from 'src/components/atoms/tooltips/ChartTooltip';

type ThemeColors = {
  colors: {
    chart: { line: { blue: string } };
    icon: { green: string };
  };
};

export type DailySpendWithROIData = {
  date: string;
  utcEpoch: number;
  dailySpend: number;
  roi: number;
};

export type DailySpendWithROIChartProps = {
  data: DailySpendWithROIData[];
  isLoading?: boolean;
  isError?: boolean;
};

export const DailySpendWithROIChart: React.FC<DailySpendWithROIChartProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const theme = useTheme();
  const axisStyles = useAxisStyles();
  const xAxisFormatter = useGetDateAxisFormatter();

  if (isLoading) {
    return (
      <Box
        height="350px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (isError || data.length === 0) {
    return (
      <Box
        height="350px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>No data available</Text>
      </Box>
    );
  }

  return (
    <Box height="300px" width="100%">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="dailySpendGradient" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={theme.colors.chart.bar.lightGreen}
                stopOpacity={0.7}
              />
              <stop
                offset="100%"
                stopColor={theme.colors.chart.bar.lightGreen}
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            strokeWidth={0.5}
            stroke={theme.colors.chart.grid.light}
            fill="transparent"
          />
          <XAxis
            dataKey="utcEpoch"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={xAxisFormatter}
            scale="linear"
            padding={{ left: 40, right: 40 }}
            {...axisStyles}
          />
          {/* Left Y-axis for Daily Spend */}
          <YAxis yAxisId="spend" width={70} domain={[0, 120]} {...axisStyles} />
          {/* Right Y-axis for ROI */}
          <YAxis
            yAxisId="roi"
            orientation="right"
            width={70}
            domain={[0, 1.6]}
            tickFormatter={(value) => value.toFixed(1)}
            {...axisStyles}
            tick={{ ...axisStyles.tick, fill: theme.colors.icon.green }}
          />
          <Tooltip
            offset={25}
            content={
              <ChartTooltip
                getEntryLabel={(dataKey) =>
                  dataKey === 'roi' ? 'ROI' : 'Daily Spend'
                }
                getEntryColor={(dataKey, themeColors) =>
                  dataKey === 'roi'
                    ? (themeColors as ThemeColors).colors.chart.line.blue
                    : (themeColors as ThemeColors).colors.icon.green
                }
                valueFormatter={(value, dataKey) =>
                  dataKey === 'roi' ? value.toFixed(2) : value.toString()
                }
              />
            }
          />

          {/* Daily Spend bars */}
          <Bar
            yAxisId="spend"
            dataKey="dailySpend"
            fill="url(#dailySpendGradient)"
            name="Daily Spend"
            radius={[8, 8, 0, 0]}
            maxBarSize={70}
          />

          {/* ROI line */}
          <Line
            yAxisId="roi"
            type="monotone"
            dataKey="roi"
            stroke={theme.colors.chart.line.blue}
            strokeWidth={3}
            dot={
              <CustomActiveDot
                fill={theme.colors.chart.line.blue}
                stroke={theme.colors.chart.line.blue}
              />
            }
            activeDot={
              <CustomActiveDot
                fill={theme.colors.chart.line.blue}
                stroke={theme.colors.chart.line.blue}
              />
            }
            connectNulls={false}
            name="ROI"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
